import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { FloatingOrbs } from './FloatingOrbs';
const platforms = [{
  name: 'Salesforce',
  color: '#00A1E0'
}, {
  name: 'AWS',
  color: '#FF9900'
}, {
  name: 'Azure',
  color: '#0078D4'
}, {
  name: 'Google Cloud',
  color: '#4285F4'
}, {
  name: 'Make.com',
  color: '#6B4FBB'
}, {
  name: 'Clay',
  color: '#FF6B6B'
}, {
  name: 'Zapier',
  color: '#FF4A00'
}, {
  name: 'n8n',
  color: '#EA4B71'
}, {
  name: 'OpenAI',
  color: '#10A37F'
}, {
  name: 'Anthropic',
  color: '#D4A574'
}];
export const SalesforceArchitecture: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const titleY = useTransform(scrollYProgress, [0, 0.3], [50, 0]);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.3], [0, 1]);
  // Very subtle sunrise glow continuation
  const sunriseOpacity = useTransform(scrollYProgress, [0, 0.4, 0.8, 1], [0.08, 0.05, 0.03, 0]);
  // Duplicate platforms array for seamless loop
  const duplicatedPlatforms = [...platforms, ...platforms, ...platforms];
  return <section id="salesforce-architecture" ref={sectionRef} className="relative py-24 md:py-32 overflow-hidden" data-id="element-319">
      {/* Floating Energy Orbs */}
      <FloatingOrbs count={12} data-id="element-320" />

      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-signal-indigo/10 to-transparent pointer-events-none" data-id="element-321" />

      {/* Subtle sunrise glow continuation */}
      <motion.div className="absolute top-0 left-0 right-0 h-[40vh] pointer-events-none" style={{
      background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(250, 255, 49, 0.06) 0%, transparent 70%)',
      opacity: sunriseOpacity
    }} data-id="element-322" />

      {/* Bottom glow */}
      <motion.div className="absolute bottom-0 left-0 right-0 h-[50vh] pointer-events-none" style={{
      background: 'radial-gradient(ellipse 100% 60% at 50% 100%, rgba(250, 255, 49, 0.12) 0%, transparent 60%)',
      opacity: sunriseOpacity
    }} data-id="element-323" />

      {/* Ambient yellow accents */}
      <div className="absolute inset-0 pointer-events-none opacity-20" data-id="element-324">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-electric-yellow/6 rounded-full blur-3xl" data-id="element-325" />
      </div>

      <div className="container mx-auto px-4 md:px-6" data-id="element-326">
        <motion.div className="text-center mb-16 max-w-4xl mx-auto" style={{
        y: titleY,
        opacity: titleOpacity
      }} data-id="element-327">
          <motion.h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold tracking-wider mb-6 text-white leading-tight" initial={{
          scale: 0.8,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }} data-id="element-328">
            Built on the platforms powering{' '}
            <span className="text-electric-yellow" data-id="element-329">
              modern intelligent systems
            </span>
          </motion.h2>
          <motion.p className="text-lg text-white/80 max-w-3xl mx-auto" initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} viewport={{
          once: true
        }} data-id="element-330">
            We work across the leading cloud, AI, automation, and data platforms
            to design and evolve intelligent Salesforce systems.
          </motion.p>
        </motion.div>

        {/* Continuous scrolling logo strip */}
        <motion.div className="relative mt-12" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 0.4
      }} viewport={{
        once: true
      }} data-id="element-331">
          {/* Gradient fade on edges */}
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-deep-navy to-transparent z-10 pointer-events-none" data-id="element-332" />
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-deep-navy to-transparent z-10 pointer-events-none" data-id="element-333" />

          {/* Scrolling container */}
          <div className="overflow-hidden py-8" data-id="element-334">
            <motion.div className="flex gap-12 items-center" animate={{
            x: [0, -1920]
          }} transition={{
            x: {
              duration: 40,
              repeat: Infinity,
              ease: 'linear'
            }
          }} style={{
            width: 'max-content'
          }} data-id="element-335">
              {duplicatedPlatforms.map((platform, index) => <motion.div key={`${platform.name}-${index}`} className="flex-shrink-0 px-8 py-4 rounded-lg border backdrop-blur-sm" style={{
              backgroundColor: 'rgba(28, 20, 61, 0.4)',
              borderColor: `${platform.color}40`
            }} whileHover={{
              backgroundColor: 'rgba(28, 20, 61, 0.7)',
              borderColor: platform.color,
              scale: 1.05
            }} transition={{
              duration: 0.2
            }} data-id="element-336">
                  <div className="text-xl font-heading font-semibold whitespace-nowrap" style={{
                color: platform.color,
                textShadow: `0 0 20px ${platform.color}40`
              }} data-id="element-337">
                    {platform.name}
                  </div>
                </motion.div>)}
            </motion.div>
          </div>
        </motion.div>

        {/* Bottom note */}
        <motion.div className="mt-12 text-center" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} viewport={{
        once: true
      }} data-id="element-338">
          <p className="text-white/60 text-sm" data-id="element-339">
            Platform-agnostic expertise across the modern technology stack
          </p>
        </motion.div>
      </div>
    </section>;
};